import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  AppBar,
  Toolbar,
  IconButton,
  Button,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Container,
  ListItemButton,
  ListItemIcon,
  Divider,
  Chip,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Add as AddIcon, Delete as DeleteIcon, Label as TagIcon } from '@mui/icons-material';
import EmptyState from '../components/common/EmptyState';
import { useNavigate } from 'react-router-dom';
import { getTags, createTag, updateTag, deleteTag } from '../services/tags';
import { useAuth } from '../hooks/useAuth';
import { useAppContext } from '../contexts/AppContext';
import { useLang } from '../contexts/langContext';

const TagsPage: React.FC = () => {
  const { lang } = useLang();
  const [tags, setTags] = useState<any[]>([]);
  const [newTagName, setNewTagName] = useState('');
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [tagToDelete, setTagToDelete] = useState<any>(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { setAppTitle } = useAppContext();

  useEffect(() => {
    fetchTags();
  }, []);

  useEffect(() => {
    setAppTitle(lang === 'en' ? 'Manage Tags' : '管理标签');
  }, []);

  const fetchTags = async () => {
    const fetchedTags = await getTags();
    setTags(fetchedTags);
  };

  const handleCreateTag = async () => {
    if (newTagName.trim()) {
      await createTag({ name: newTagName, public: false });
      setNewTagName('');
      fetchTags();
    }
  };

  const handleDeleteTag = async () => {
    if (tagToDelete) {
      await deleteTag(tagToDelete._id);
      setIsDeleteDialogOpen(false);
      setTagToDelete(null);
      fetchTags();
    }
  };

  return (
    <Container maxWidth="sm">
      <Container sx={{ p: [2, 3] }} maxWidth="md">
        <Box sx={{ display: 'flex', mb: 2 }}>
          <TextField
            value={newTagName}
            onChange={(e) => setNewTagName(e.target.value)}
            label={lang === 'en' ? 'New Tag Name' : '新标签名称'}
            variant="outlined"
            size="small"
            sx={{ mr: 1, flexGrow: 1 }}
          />
          <Button
            onClick={handleCreateTag}
            variant="contained"
            startIcon={<AddIcon />}
            disabled={!newTagName.trim()}
          >
            {lang === 'en' ? 'Add Tag' : '添加标签'}
          </Button>
        </Box>
        {tags.length > 0 ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
            {tags.map((tag) => (
              <Chip
                key={tag._id}
                icon={<TagIcon />}
                label={tag.name}
                onDelete={() => {
                  setTagToDelete(tag);
                  setIsDeleteDialogOpen(true);
                }}
                color={tag.public ? "primary" : "default"}
                variant="outlined"
                onClick={() => user?.role === 'Admin' && updateTag(tag._id, { ...tag, public: !tag.public })}
                sx={{ m: 0.5 }}
              />
            ))}
          </Box>
        ) : (
          <EmptyState
            icon={TagIcon}
            message={
              lang === 'en'
                ? 'No tags yet.\nTags help you organize your slides by grouping related ones together.\nYou can make tags public (visible to everyone) or keep them private.\nUse the field above to create your first tag.'
                : '暂无标签。\n标签可以帮助您按类别组织幻灯片、模板、图片和材料等。\n请在上方输入框创建您的第一个标签。'
            }
            sx={{ mt: 4 }}
          />
        )}
      </Container>
      <Dialog open={isDeleteDialogOpen} onClose={() => setIsDeleteDialogOpen(false)}>
        <DialogTitle>{lang === 'en' ? 'Confirm Delete' : '确认删除'}</DialogTitle>
        <DialogContent>
          {lang === 'en' ? 'Are you sure you want to delete the tag "' : '你确定要删除标签 "'}
          {tagToDelete?.name}"?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteDialogOpen(false)}>{lang === 'en' ? 'Cancel' : '取消'}</Button>
          <Button onClick={handleDeleteTag} color="error">
            {lang === 'en' ? 'Delete' : '删除'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TagsPage;